import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  FilePlus2,
  FileText,
  RefreshCw,
  Search,
  ShieldCheck,
} from 'lucide-react';
import { toast } from 'react-hot-toast';

import ReportsImportDialog from '../components/Reports/ReportsImportDialog';
import { DocumentStatusBadge, RunStatusBadge } from '../components/Reports/ReportStatus';
import apiService from '../services/api';
import type { CompanySummary } from '../types';
import { analysisProfileLabel, completionPercent, reportPeriodLabel } from '../utils/reportDashboard';

const ReportsCompaniesPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  const companiesQuery = useQuery({
    queryKey: ['report-companies', debouncedSearch],
    queryFn: () => apiService.getTrackedCompanies(debouncedSearch || undefined),
  });

  const companies = useMemo<CompanySummary[]>(() => companiesQuery.data || [], [companiesQuery.data]);
  const totals = useMemo(() => ({
    reports: companies.reduce((sum, company) => sum + company.report_count, 0),
    completed: companies.filter((company) => company.latest_report?.latest_run?.status === 'done').length,
    processing: companies.filter((company) => company.latest_report?.document_status !== 'processed').length,
  }), [companies]);

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-7 sm:px-6 sm:py-9 lg:px-8">
      <header className="flex flex-col gap-5 border-b border-gray-200 pb-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2 text-xs font-medium text-primary-700">
            <ShieldCheck className="h-4 w-4" aria-hidden="true" />
            סביבת חילוץ מאומתת
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-950 sm:text-3xl">חברות במעקב</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-600">
            דוחות תקופתיים, מצב עיבוד ושלמות הקורפוס במקום אחד. כל ערך מספרי נשמר עם מקור ובדיקת עיגון.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setImportOpen(true)}
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
        >
          <FilePlus2 className="h-4 w-4" />
          ייבוא דוח חדש
        </button>
      </header>

      <section className="mt-6 grid overflow-hidden rounded-2xl border border-gray-200 bg-white sm:grid-cols-4" aria-label="סיכום מעקב">
        {[
          { label: 'חברות', value: companies.length, icon: Building2 },
          { label: 'דוחות', value: totals.reports, icon: FileText },
          { label: 'ריצה אחרונה הושלמה', value: totals.completed, icon: ShieldCheck },
          { label: 'דוחות בעיבוד', value: totals.processing, icon: RefreshCw },
        ].map((metric, index) => {
          const Icon = metric.icon;
          return (
            <div key={metric.label} className={`flex items-center gap-3 px-5 py-4 ${index ? 'border-t border-gray-100 sm:border-r sm:border-t-0' : ''}`}>
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-50 text-gray-500">
                <Icon className="h-4 w-4" aria-hidden="true" />
              </span>
              <div>
                <div className="text-xl font-semibold tabular-nums text-gray-950">{metric.value}</div>
                <div className="text-xs text-gray-500">{metric.label}</div>
              </div>
            </div>
          );
        })}
      </section>

      <section className="mt-6" aria-labelledby="companies-table-title">
        <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 id="companies-table-title" className="text-sm font-semibold text-gray-900">תיק החברות</h2>
            <p className="mt-0.5 text-xs text-gray-500">לחיצה על חברה פותחת את ציר הזמן והדוחות שלה.</p>
          </div>
          <label className="relative block w-full sm:w-72">
            <span className="sr-only">חיפוש חברה</span>
            <Search className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="חיפוש חברה"
              className="h-11 w-full rounded-xl border border-gray-300 bg-white pr-9 pl-3 text-sm outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-500/15"
            />
          </label>
        </div>

        {companiesQuery.isLoading ? (
          <div className="space-y-2" aria-label="טוען חברות">
            {[0, 1, 2].map((item) => <div key={item} className="h-24 animate-pulse rounded-xl border border-gray-100 bg-white motion-reduce:animate-none" />)}
          </div>
        ) : companiesQuery.isError ? (
          <div className="rounded-2xl border border-error-200 bg-error-50 px-5 py-8 text-center">
            <p className="text-sm font-medium text-error-800">לא הצלחנו לטעון את החברות במעקב.</p>
            <button
              type="button"
              onClick={() => companiesQuery.refetch()}
              className="mt-3 inline-flex min-h-11 items-center gap-2 rounded-lg px-4 text-sm font-medium text-error-800 hover:bg-error-100"
            >
              <RefreshCw className="h-4 w-4" /> נסה שוב
            </button>
          </div>
        ) : companies.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-14 text-center">
            <Building2 className="mx-auto h-10 w-10 text-gray-300" />
            <h2 className="mt-4 text-base font-semibold text-gray-800">
              {debouncedSearch ? 'לא נמצאו חברות שמתאימות לחיפוש' : 'עדיין אין חברות במעקב'}
            </h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-gray-500">
              {debouncedSearch ? 'נסה שם אחר או נקה את שדה החיפוש.' : 'ייבא דוח ממאיה כדי ליצור חברה ולהתחיל מעקב.'}
            </p>
            {!debouncedSearch && (
              <button
                type="button"
                onClick={() => setImportOpen(true)}
                className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-xl bg-primary-600 px-4 text-sm font-semibold text-white hover:bg-primary-700"
              >
                <FilePlus2 className="h-4 w-4" /> ייבוא דוח ראשון
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
            <ul className="divide-y divide-gray-100">
              {companies.map((company) => {
                const report = company.latest_report;
                const run = report?.latest_run;
                const percent = completionPercent(run?.stats);
                return (
                  <li key={company.id}>
                    <Link
                      to={`/reports/companies/${company.id}`}
                      className="group grid min-h-[96px] gap-4 px-4 py-4 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 sm:grid-cols-[minmax(220px,1.4fr)_minmax(180px,1fr)_minmax(200px,1fr)_44px] sm:items-center sm:px-5"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-700">
                          <Building2 className="h-5 w-5" />
                        </span>
                        <div className="min-w-0">
                          <h3 className="truncate text-sm font-semibold text-gray-950">{company.name}</h3>
                          <p className="mt-1 text-xs text-gray-500">{analysisProfileLabel(company.analysis_profile)} · {company.report_count} דוחות</p>
                        </div>
                      </div>

                      <div>
                        <div className="text-xs font-medium text-gray-500">דוח אחרון</div>
                        <div className="mt-1 text-sm text-gray-800">{report ? reportPeriodLabel(report) : '—'}</div>
                        {report && <div className="mt-2"><DocumentStatusBadge status={report.document_status} progress={report.processing_progress} /></div>}
                      </div>

                      <div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-medium text-gray-500">שלמות חילוץ</span>
                          {run && <RunStatusBadge status={run.status} />}
                        </div>
                        <div className="mt-2 flex items-center gap-3">
                          <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100" aria-label={`שלמות ${percent}%`}>
                            <div className="h-full rounded-full bg-primary-600 transition-[width]" style={{ width: `${percent}%` }} />
                          </div>
                          <span className="w-9 text-left text-xs font-semibold tabular-nums text-gray-700">{run ? `${percent}%` : '—'}</span>
                        </div>
                      </div>

                      <span className="flex h-11 w-11 items-center justify-center rounded-lg text-gray-400 transition group-hover:bg-white group-hover:text-primary-700">
                        <ArrowLeft className="h-5 w-5" aria-hidden="true" />
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </section>

      <ReportsImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onCreated={async (report) => {
          setImportOpen(false);
          toast.success('הדוח נוסף למעקב והעיבוד החל');
          await queryClient.invalidateQueries({ queryKey: ['report-companies'] });
          navigate(`/reports/companies/${report.company_id}`);
        }}
      />
    </div>
  );
};

export default ReportsCompaniesPage;
